import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { LoginService } from 'src/app/login/login.service';
import { ActionLoaderComponent } from '../action-loader/action-loader.component';
import { NotificationService } from '../notification.service';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { E164_PHONE_VALIDATOR } from '../phone-input/e164.validator';
import { PhoneInputComponent } from '../phone-input/phone-input.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-dialog-changepwd',
  templateUrl: 'dialog-request-access-code.html',
  styles: [
    'mat-form-field.mat-form-field {  width: 100%  }',
    '.dialog-request-actions {      align-items: flex-start;  }',
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ActionLoaderComponent,
    PhoneInputComponent,
    DigitOnlyModule
  ]
})
export class DialogRequestAccessCodeComponent {
  private readonly fb = inject(FormBuilder);
  private readonly loginService = inject(LoginService);
  private readonly notificationService = inject(NotificationService);
  private readonly dialogRef = inject(MatDialogRef<DialogRequestAccessCodeComponent>);
  private readonly data = inject(MAT_DIALOG_DATA);

  public form: FormGroup | undefined;
  @ViewChild('saveRequest') saveRequest: ActionLoaderComponent | undefined;

  constructor() {
    this.buildForm();
  }

  public onSave() {
    if (!this.form?.valid) {
      return;
    }

    this.saveRequest!.start();
    this.loginService
      .sendUserRequest(this.form.value)
      .then((res: any) => {
        if (res.code) {
          this.notificationService.success('F002', [
            { key: 'CODE', val: res.code },
            { key: 'NUMBER', val: this.data.contactNumber },
          ]);
        } else {
          this.notificationService.success('F005', [
            { key: 'NUMBER', val: this.data.contactNumber },
          ]);
        }

        this.dialogRef.close();
      })
      .catch((err) => {
        if (err.error.reqId) {
          const phoneNum = err.error.phone
            ? err.error.phone
            : this.form!.value.phone;
          const messageKey = err.error.phone ? 'F009' : 'F007';
          this.notificationService
            .askFor(messageKey, [{ key: 'PHONE_NUM', val: phoneNum }])
            .then((askResponse) => {
              if (askResponse.value) {
                this.saveRequest!.start();
                this.loginService
                  .remindRequest(err.error.reqId)
                  .then(() => {
                    this.notificationService.success('F008');
                    this.dialogRef.close();
                  })
                  .catch(() => this.notificationService.error(err))
                  .finally(() => this.saveRequest!.stop());
              }
            });

          return;
        }

        this.notificationService.error(err);
      })
      .finally(() => this.saveRequest!.stop());
  }

  public onCancel() {
    this.dialogRef.close();
  }

  private buildForm() {
    this.form = this.fb.group({
      name: [null, Validators.required],
      lastname: [null, Validators.required],
      email: [
        null,
        [Validators.required, Validators.email],
      ],
      phone: [
        null,
        [
          Validators.required,
          E164_PHONE_VALIDATOR,
        ],
      ],
    });
  }
}
